import {
  AdvancedFiltersMainWrapper,
  EnquiryHandle,
  IServerSideGetRowsParams,
  Product,
  ServerSideCriteria,
  standaloneExternalContext,
  useServerEnquiry,
  Column,
} from '@access-erp/common';
import { Guid } from 'guid-typescript';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { Forms } from '@/graphql/contracts/_generated_/contracts';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { SalesOrderWithId } from '@/utils/data-helpers/types';
import { useColumns } from '@/pages/SalesOrders/hooks/useColumns';
import useGetBCSalesOrders from '@/pages/SalesOrders/hooks/useGetBCSalesOrders';
import { SalesOrderPayloadModal } from '@/pages/SalesOrders/components/SalesOrderPayload/SalesOrderPayloadModal';
import { EnquiriesApplicationModule } from '@/components/common/EnquiriesScreen/EnquiriesApplicationModule';
import { ContractsExternalContext } from '@/components/common/EnquiriesScreen/utils/enquiriesSettings';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { getSubscriptionId } from '@/utils/auth/subscription.utils';
import { getAccessToken } from '@/utils/auth/token.utils';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { appSettings } from '@/utils/microfrontend/appSettingsForEnquiriesScreen';

export default function SalesOrdersTabContainer() {
  const gridRef = useRef<EnquiryHandle<SalesOrderWithId>>(null);
  const { contractId }: { contractId: UUID } = useParams();
  const { getValue } = useAddQueryToUrl();
  const [isPayloadVisible, setIsPayloadVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SalesOrderWithId | undefined>(undefined);
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const { getBCSalesOrders } = useGetBCSalesOrders();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const getBillingSO = getBCSalesOrders;

  const contractIdRef = useRef<string>(contractId);

  useEffect(() => {
    contractIdRef.current = contractId;
  }, [contractId]);

  const [subscriptionId, setSubscriptionId] = useState<string>();

  useEffect(() => {
    const getSubId = async () => {
      const subId = await getSubscriptionId();
      setSubscriptionId(subId!);
    };
    getSubId();
  }, []);

  useEffect(() => {
    const currentTab = getValue('tab');
    const isActiveTab = currentTab === 'Sales-Orders';
    const enquiryMainWrapper = document.querySelector('.enquiry-main-wrapper');

    if (enquiryMainWrapper && enquiryMainWrapper.firstElementChild) {
      const firstChild = enquiryMainWrapper.firstElementChild as HTMLElement;
      if (isActiveTab) {
        firstChild.style.paddingTop = '15px';
        firstChild.style.paddingBottom = '10px';
      } else {
        firstChild.style.paddingTop = '';
        firstChild.style.paddingBottom = '';
      }
    }
  });

  const externalContext: ContractsExternalContext = {
    ...standaloneExternalContext,
    product: Product.ContractsAndBilling,
    getIdentityToken: async () => getAccessToken(),
    refetchToken: async () => '',
    getAccessWorkspaceInstanceId: () => subscriptionId!,
    preloadedData: {},
  };

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const baseHeaderColumns = useColumns(true).headerColumns as any[];
  const { headerColumns } = useMemo(() => {

    const withoutContractNumber = baseHeaderColumns.filter(
      (col: any) => col?.name !== 'contractNumber' && col?.caption !== 'Contract Number'
    );

    const payloadColumn: Column<any> = {
      headerName: '',
      field: '__payload__',
      pinned: 'left',
      name: 'payload',
      caption: 'Payload',
      width: 120,
      cellRenderer: ({ data }: any) => {
        return (
          <a
            className="btn btn-link"
            style={{ textDecoration: 'none' }}
            onClick={(e) => {
              e.preventDefault();
              setSelectedRow(data);
              setIsPayloadVisible(true);
            }}
          >
            View Payload
          </a>
        );
      },
      suppressMenu: true,
      filterable: false,
      groupable: false,
      sortable: false,
      suppressAdvancedFilter: true,
      suppressMovable: true,
      resizable: false,
      disableRowGroup: true,
    } as unknown as Column<any>;

    return { headerColumns: [payloadColumn, ...withoutContractNumber] };
  }, [baseHeaderColumns]);

  const enquiry = useServerEnquiry<SalesOrderWithId, any>({
    gridRef,
    headerColumns,
    initialFilter: savedFilters,
  });

  useEffect(() => {
    if (enquiry) {
      enquiry?.refreshSearch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const getData = async (
    params: IServerSideGetRowsParams<SalesOrderWithId>,
    serverSideCriteria: ServerSideCriteria
  ) => {
    await getSavedFilterIfAny(Forms.ContractDetailsSalesOrdersByBc, params);

    const response = await getBillingSO({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        status: enquiry.serverSide?.current?.customFilters?.status,
        ...serverSideCriteria.groupingFilters,
        contractId: { eq: contractIdRef.current }
      } as any,
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    });

    setPageIsLoading(false);

    const items =
      response?.items
        ?.map((el: any) => ({
          ...el,
          id:
            el?.salesOrderID && !Guid.parse(el.salesOrderID).isEmpty()
              ? el.salesOrderID
              : Guid.create().toString(),
        })) ?? [];

    const result = {
      items,
      pageInfo: {
        hasNextPage: response?.pageInfo.hasNextPage,
        hasPreviousPage: response?.pageInfo?.hasPreviousPage,
        endCursor: response?.pageInfo?.endCursor,
        startCursor: response?.pageInfo?.startCursor,
      },
    };

    return result;
  };

  if (!subscriptionId) {
    return <div />;
  }

  return (
    <div>
      <EnquiriesApplicationModule
        externalContext={externalContext}
        appSettings={appSettings}
        pageName="Sales Orders"
        suppressIntercom={true}
        apolloClient={apolloClientContracts}
        gridContainerStyle={{ height: '600px', marginBottom: '20px', boxShadow: '0 2px 3px rgba(0, 0, 0, 0.16)' }}
      >
        <AdvancedFiltersMainWrapper>
          <ContractsEnquiriesScreen
            ref={gridRef}
            enquiry={enquiry}
            filterForm={Forms.ContractDetailsSalesOrdersByBc}
            formName={Forms.ContractDetailsSalesOrdersByBc}
            headerProps={{
              searchPlaceholder: 'Start typing Contract Number or Account Name',
              showExportAll: false,
              showExportCurrent: false,
              search: getSavedSearch?.()?.savedSearch,
            }}
            detailProps={{
              exportFileName: 'Sales Orders',
              rowGroupPanelShow: 'always',
              getData: getData as any,
              pageSize: CONTRACTS_PER_PAGE,
              rowSelection: 'none',
              suppressRowClickSelection: true,
              loading: pageIsLoading
            }}
          />
        </AdvancedFiltersMainWrapper>
      </EnquiriesApplicationModule>
      <>
        {isPayloadVisible && selectedRow ? (
          <SalesOrderPayloadModal
            handleClose={() => setIsPayloadVisible(false)}
            isVisible={isPayloadVisible}
            selectedRow={selectedRow}
            isBillingCyclesPath={true}
          />
        ) : null}
        <Toaster />
      </>
    </div>
  );
}
